import { _ } from "libs";
import React, { useEffect, useState, useRef } from "react";
import { Card, SubscriptionPlan } from "../../types";
import { loadSubscriptionPlans, subscribeToPlan, cancelSubscriptionPlan, resubscribeToPlan } from "../../actions";
import Loader from "../loader";
import cx from "classnames";
import PaymentModal from "../paymentModal";
import Currency from "../currency";
import DropdownIconMenu from "../dropdownIconMenu";
import moment from "moment";

const SubscriptionList = () => {
	const [plans, setPlans] = useState(null as SubscriptionPlan[]);
	const modal = useRef<typeof PaymentModal>(null as typeof PaymentModal);
	const [loading, setLoading] = useState(false);

	const updatePlans = () => {
		loadSubscriptionPlans().then((plans) => {
			setPlans(Object.values(plans));
			setLoading(false);
		});
	};

	useEffect(() => {
		let unmounted = false;
		if (!unmounted && plans === null) {
			updatePlans();
		}
		return () => {
			unmounted = true;
		};
	}, [plans]);

	if (plans === null || loading === true) {
		return <Loader />;
	}

	const onPayClicked = (card: Card, voucher: string, promotionId: string, data) => {
		setLoading(true);
		subscribeToPlan(data.planId, { card: card.cardId, voucher }).then((res) => {
			updatePlans();
		});
	};

	const cancelSubscription = (subscription: SubscriptionPlan) => {
		setLoading(true);
		cancelSubscriptionPlan(subscription.subscription_id).then(updatePlans);
	};

	const resubscribeSubscription = (subscription: SubscriptionPlan) => {
		setLoading(true);
		resubscribeToPlan(subscription.subscription_id).then(updatePlans);
	};

	return (
		<div className="table defaultSpacing">
			{plans.map((subscription, key) => {
				const className = cx("cell", {
					clickable: subscription.subscribed === false && subscription.available === true,
				});
				if (!subscription) {
					return null;
				}
				let price = subscription.year ? subscription.year.amount / 100 / 12 : 0;
				let endDate: string;
				if (subscription.subscribed && subscription.subscription) {
					endDate = moment.unix(subscription.subscription.current_period_end).format("MMMM D, YYYY");
				}
				return (
					<div
						key={key}
						className={className}
						onClick={() => {
							if (!subscription.available) {
								return;
							}
							if (subscription.subscribed === false) {
								modal.current.setPlan(subscription);
								modal.current.open();
							}
						}}
					>
						<div className="item subscription large">
							<div className="itemBody">
								<div className="title">{subscription?.product?.name}</div>
								<div className="description">
									{_(subscription?.product?.metadata.description_label)}
								</div>
								<div className="price">
									<Currency
										amount={price}
										currency={subscription.year.currency?.toUpperCase() || "CHF"}
									></Currency>{" "}
									/ {_("month")}
								</div>
								{subscription.subscribed && !subscription.cancel_at_period_end ? (
									<div className="meta">{_("subscription.renews", null, { date: endDate })}</div>
								) : null}
								{subscription.subscribed && subscription.cancel_at_period_end ? (
									<div className="meta">{_("subscription.ends", null, { date: endDate })}</div>
								) : null}
								{!subscription.available ? (
									<div className="meta">{_("subscription.notAvailable")}</div>
								) : null}
							</div>
							<div className="itemExtra">
								{subscription.subscribed === true ? (
									<div className="attributeBadge turquoise checkmark">{_("subscription.active")}</div>
								) : null}
							</div>
							<div className="itemMenu">
								{subscription.subscribed && !subscription.cancel_at_period_end ? (
									<DropdownIconMenu>
										<span onClick={() => cancelSubscription(subscription)}>
											{_("subscription.cancel")}
										</span>
									</DropdownIconMenu>
								) : null}
								{subscription.subscribed && subscription.cancel_at_period_end ? (
									<DropdownIconMenu>
										<span onClick={() => resubscribeSubscription(subscription)}>
											{_("subscription.resubscribe")}
										</span>
									</DropdownIconMenu>
								) : null}
							</div>
						</div>
					</div>
				);
			})}
			<PaymentModal ref={modal} onPayClicked={onPayClicked} subscriptionMode={true} />
		</div>
	);
};

export default SubscriptionList;
